/**
 * Health Service - Handles CRUD operations for health providers using Firestore
 */

import {
  collection,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  onSnapshot,
  Unsubscribe
} from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { HealthProvider, resolveProviderType } from '../types/health';

/**
 * Check if user is authenticated with Firebase
 * Returns true if there's a current Firebase user AND localStorage indicates authenticated
 */
function isAuthenticated(): boolean {
  const hasFirebaseUser = !!auth.currentUser;
  const hasLocalAuth = localStorage.getItem('isAuthenticated') === 'true';
  return hasFirebaseUser && hasLocalAuth;
}

/** Returns the subcollection ref for a profile's health providers. */
function healthCollection(profileId: string) {
  return collection(db, 'profiles', profileId, 'health');
}

/** Returns a doc ref within the health subcollection. */
function healthDoc(profileId: string, docId: string) {
  return doc(db, 'profiles', profileId, 'health', docId);
}

/** Recursively strip undefined values — Firestore rejects undefined at any depth. */
function removeUndefined(obj: unknown): unknown {
  if (Array.isArray(obj)) {
    return obj.map(item => removeUndefined(item));
  }
  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>)
        .filter(([_, v]) => v !== undefined)
        .map(([k, v]) => [k, removeUndefined(v)])
    );
  }
  return obj;
}

class HealthService {
  private cachedProviders: Map<string, HealthProvider[]> = new Map();
  private unsubscribes: Map<string, Unsubscribe> = new Map();
  private subscriberCallbacks: Map<string, (providers: HealthProvider[]) => void> = new Map();
  /**
   * Serializes ALL Firestore writes per document ID. Every write (create, update)
   * chains after the previous write for the same document, preventing races where
   * updateDoc fires before setDoc, or two updateDoc calls overwrite each other.
   */
  private _pendingWrites = new Map<string, Promise<void>>();

  /** Notify active subscriber with current cache (instant UI update after mutations) */
  private notifySubscribers(profileId: string) {
    const callback = this.subscriberCallbacks.get(profileId);
    const cached = this.cachedProviders.get(profileId) || [];
    if (callback) callback([...cached]);
  }

  /**
   * Pre-fetch health providers so data is cached before page mounts.
   * Called during app initialization in ProfileContext.
   */
  async prefetch(profileId: string): Promise<void> {
    if (!isAuthenticated()) return;
    if (this.cachedProviders.has(profileId)) return; // Already cached
    await this.listAsync(profileId);
  }

  // Subscribe to real-time updates for a profile's health providers
  subscribeToProviders(profileId: string, callback: (providers: HealthProvider[]) => void): Unsubscribe {
    // CRITICAL: Check authentication before making Firestore calls
    // This prevents permission errors when user is not logged in (e.g., Private Guest Mode)
    if (!isAuthenticated()) {
      setTimeout(() => callback([]), 0);
      return () => {}; // Return no-op unsubscribe
    }

    // Store callback for instant notification on cache mutations
    this.subscriberCallbacks.set(profileId, callback);

    // Immediately return pre-fetched data if available (instant load)
    // Includes empty arrays — so empty state shows instantly after prefetch
    const prefetched = this.cachedProviders.get(profileId);
    if (prefetched) {
      setTimeout(() => callback(prefetched), 0);
    }

    // Unsubscribe from previous listener if exists
    const existingUnsub = this.unsubscribes.get(profileId);
    if (existingUnsub) {
      existingUnsub();
    }

    const unsubscribe = onSnapshot(healthCollection(profileId), (snapshot) => {
      const providers: HealthProvider[] = [];
      snapshot.forEach((docSnap) => {
        providers.push({ id: docSnap.id, ...docSnap.data() } as HealthProvider);
      });
      // Sort by name
      providers.sort((a, b) => a.name.localeCompare(b.name));
      this.cachedProviders.set(profileId, providers);
      callback(providers);
    }, (_error) => {
      // Silently handle permission errors - they're expected during auth token validation
      // Don't call callback with empty array - it would overwrite successfully loaded data
      // The initial listAsync fetch will have already loaded the data
    });

    this.unsubscribes.set(profileId, unsubscribe);
    return unsubscribe;
  }

  // Get all health providers for a profile (async, from Firestore)
  async listAsync(profileId: string): Promise<HealthProvider[]> {
    // CRITICAL: Check authentication before making Firestore calls
    if (!isAuthenticated()) {
      return [];
    }

    try {
      const snapshot = await getDocs(healthCollection(profileId));

      const providers: HealthProvider[] = [];
      snapshot.forEach((docSnap) => {
        providers.push({ id: docSnap.id, ...docSnap.data() } as HealthProvider);
      });

      // Sort by name
      providers.sort((a, b) => a.name.localeCompare(b.name));
      this.cachedProviders.set(profileId, providers);
      return providers;
    } catch (error) {
      console.error('Error loading health providers:', error);
      return [];
    }
  }

  // Get all health providers (sync, from cache - for backwards compatibility)
  list(profileId: string): HealthProvider[] {
    return this.cachedProviders.get(profileId) || [];
  }

  // Create new health provider
  create(profileId: string, provider: Omit<HealthProvider, 'id' | 'createdAt' | 'updatedAt'>): HealthProvider {
    // Check for duplicate health provider (same name + resolved provider type)
    const cached = this.cachedProviders.get(profileId) || [];
    const newType = resolveProviderType(provider as HealthProvider);
    const isDuplicate = cached.some(existing =>
      existing.name.toLowerCase().trim() === provider.name.toLowerCase().trim() &&
      resolveProviderType(existing) === newType
    );

    if (isDuplicate) {
      throw new Error('This health provider already exists in your profile');
    }

    const id = `hp_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    const now = new Date().toISOString();

    const newProvider: HealthProvider = {
      ...provider,
      id,
      createdAt: now,
    };

    // If this is primary, unset others
    if (newProvider.isPrimary) {
      cached.forEach(p => {
        if (p.isPrimary) {
          updateDoc(healthDoc(profileId, p.id), { isPrimary: false }).catch(error => {
            console.error('Error unsetting primary provider:', error);
          });
        }
      });
    }

    // Strip undefined values (deep) — Firestore rejects undefined in setDoc()
    const firestoreData = removeUndefined(newProvider) as Record<string, unknown>;

    // Save to Firestore (async, but we return immediately for responsive UI).
    // Serialize via _pendingWrites so subsequent update() calls wait for this to complete.
    const prev = this._pendingWrites.get(id) || Promise.resolve();
    const writePromise = prev.then(() =>
      setDoc(healthDoc(profileId, id), firestoreData)
    ).catch(error => {
      console.error('Error saving health provider to Firestore:', error);
    });
    this._pendingWrites.set(id, writePromise);

    // Update cache immediately for responsive UI
    if (newProvider.isPrimary) {
      cached.forEach(p => (p.isPrimary = false));
    }
    cached.push(newProvider);
    cached.sort((a, b) => a.name.localeCompare(b.name));
    this.cachedProviders.set(profileId, cached);
    this.notifySubscribers(profileId);

    return newProvider;
  }

  // Update health provider
  update(profileId: string, id: string, updates: Partial<HealthProvider>): HealthProvider {
    const cached = this.cachedProviders.get(profileId) || [];
    const index = cached.findIndex(p => p.id === id);
    if (index === -1) throw new Error('Provider not found');

    const now = new Date().toISOString();

    // Prepare updates for cache - remove undefined values from the merged object
    const cleanedUpdates = Object.fromEntries(
      Object.entries(updates).filter(([_, v]) => v !== undefined)
    );
    const updatedProvider = { ...cached[index], ...cleanedUpdates, updatedAt: now };

    // Check for duplicate (excluding current provider being updated)
    if (updates.name || updates.providerType) {
      const updatedType = resolveProviderType(updatedProvider as HealthProvider);
      const isDuplicate = cached.some((existing, idx) => {
        if (idx === index) return false;
        return existing.name.toLowerCase().trim() === updatedProvider.name.toLowerCase().trim() &&
          resolveProviderType(existing) === updatedType;
      });

      if (isDuplicate) {
        throw new Error('This health provider already exists in your profile');
      }
    }

    // If setting as primary, unset others
    if (updates.isPrimary) {
      cached.forEach(p => {
        if (p.id !== id && p.isPrimary) {
          p.isPrimary = false;
          updateDoc(healthDoc(profileId, p.id), { isPrimary: false }).catch(error => {
            console.error('Error unsetting primary provider:', error);
          });
        }
      });
    }

    // Update cache immediately
    cached[index] = updatedProvider;
    this.cachedProviders.set(profileId, cached);
    this.notifySubscribers(profileId);

    // Prepare Firestore updates - convert undefined to deleteField(), deep-strip nested undefined
    const firestoreUpdates: any = { updatedAt: now };
    Object.entries(updates).forEach(([key, value]) => {
      if (value === undefined) {
        firestoreUpdates[key] = deleteField();
      } else {
        firestoreUpdates[key] = removeUndefined(value);
      }
    });

    // Save to Firestore (async).
    // Serialize via _pendingWrites — waits for any prior create or update on this doc.
    const prevWrite = this._pendingWrites.get(id) || Promise.resolve();
    const updateWritePromise = prevWrite.then(() =>
      updateDoc(healthDoc(profileId, id), firestoreUpdates)
    ).catch(error => {
      console.error('Error updating health provider in Firestore:', error);
    });
    this._pendingWrites.set(id, updateWritePromise);

    return updatedProvider;
  }

  // Delete health provider
  delete(profileId: string, id: string): void {
    // Update cache immediately
    const cached = this.cachedProviders.get(profileId) || [];
    this.cachedProviders.set(profileId, cached.filter(p => p.id !== id));
    this.notifySubscribers(profileId);

    // Delete from Firestore (async)
    deleteDoc(healthDoc(profileId, id)).catch(error => {
      console.error('Error deleting health provider from Firestore:', error);
    });
  }

  // Cleanup subscriptions
  cleanup(profileId?: string): void {
    if (profileId) {
      const unsub = this.unsubscribes.get(profileId);
      if (unsub) {
        unsub();
        this.unsubscribes.delete(profileId);
      }
      this.cachedProviders.delete(profileId);
      this.subscriberCallbacks.delete(profileId);
    } else {
      // Cleanup all
      this.unsubscribes.forEach(unsub => unsub());
      this.unsubscribes.clear();
      this.cachedProviders.clear();
      this.subscriberCallbacks.clear();
    }
  }
}

export const healthService = new HealthService();
